/**
 * Play engine — SVG-based one-stroke interaction.
 *
 * Owns: graph state, drag/tap input, edge traversal validation, win detect,
 * undo, idle hints. Returns a controller the host screen wires to its
 * chrome (timer, progress, win callback).
 *
 * Rendering: SVG (≤ 70 elements → DOM is fine; gives us free a11y +
 * hit testing). The trace path is a single <polyline>.
 *
 * Per UI-SPEC §3 G2 + GAME-SPEC §1.1 mechanics:
 *  - Touch any odd-degree vertex (or any vertex if 0 odd-degree).
 *  - Drag toward a neighbor connected by an unused edge.
 *  - When pointer enters that vertex's hit-radius, commit the edge.
 *  - Long-press a vertex = reset path (only if at start or no edges done).
 */

import type { Edge, Puzzle, Vertex } from '../engine/types';

/**
 * Reconstruct the ordered vertex sequence walked by an edge-id trail, so a
 * replay can draw ONE continuous stroke that flows head → tail through shared
 * vertices (rather than independent segments). Returns `null` when the trail
 * isn't a connected walk (caller falls back to the per-segment sweep).
 *
 * Pure + exported so the reconstruction invariant is unit-testable against a
 * real puzzle's Euler trail (consecutive vertices joined by the matching edge,
 * length === edges + 1).
 */
export function vertexSeqFromEdges(edges: readonly Edge[], edgeOrder: number[]): number[] | null {
  if (edgeOrder.length === 0) return null;
  const byId = new Map(edges.map((e) => [e.id, e] as const));
  const e0 = byId.get(edgeOrder[0]!);
  if (!e0) return null;
  if (edgeOrder.length === 1) return [e0.a, e0.b];
  const e1 = byId.get(edgeOrder[1]!);
  // start = the e0 endpoint NOT shared with e1 (so the pen flows e0 → e1 → …)
  let cur: number;
  if (e1) {
    const shared = e0.a === e1.a || e0.a === e1.b ? e0.a : e0.b;
    cur = e0.a === shared ? e0.b : e0.a;
  } else {
    cur = e0.a;
  }
  const seq: number[] = [cur];
  for (const id of edgeOrder) {
    const e = byId.get(id);
    if (!e) return null;
    const next = e.a === cur ? e.b : e.b === cur ? e.a : null;
    if (next === null) return null; // discontinuity
    seq.push(next);
    cur = next;
  }
  return seq;
}

export interface PlayConfig {
  puzzle: Puzzle;
  onProgress?: (done: number, total: number) => void;
  onWin?: (result: { timeMs: number; attempts: number; path: number[] }) => void;
  onIdle?: (stage: 'wiggle' | 'hint') => void;
  /** Color theme; defaults to rarity color. */
  accentColor?: string;
  /** Reduced motion override (else system). */
  reduceMotion?: boolean;
  /**
   * v2.3 FDT-O-002 (I-19) — Euler rule hints. When false (purist mode), the
   * teaching layer is fully suppressed: no dead-end shake, no stuck toast, no
   * first-solve bubble, no idle ghost-path. The canonical rarity start-ring and
   * traversed-edge coloring always stay (they are core Claude Design, not hints).
   */
  hints?: boolean;
  /** Show the one-time first-solve bubble (host computes: hints && !hintBubbleShown). */
  firstHintBubble?: boolean;
  /** Called once when the first-solve bubble is shown, so the host can persist "shown". */
  onHintBubbleShown?: () => void;
  /** i18n labels passed in to keep play-engine i18n-agnostic. */
  hintLabels?: { startBubble: string; stuck: string; noRepeat: string };
}

export interface PlayController {
  el: SVGSVGElement;
  destroy(): void;
  reset(): void;
  /** Undo the last drawn edge (no-op if none). Restores the previous head. */
  undo(): void;
  /** Get elapsed time in ms. */
  elapsed(): number;
  /** v2.3 FDT-O-009 — animate the canonical solution trail being drawn (review/playback).
   *  `force` animates even under reduce-motion (the Review replay IS the feature). */
  playSolution(force?: boolean): void;
  /** Animate an arbitrary trail (e.g. the player's own recorded solve path).
   *  `force` animates even under reduce-motion. */
  playPath(order: number[], force?: boolean): void;
  /** Force-finalize for testing. */
  _state(): { traversed: number[]; head: number | null };
}

const VIEW = 320; // viewBox is 320x320 internally
const VERT_R = 9;
// v2.2 P-3.1 — Sticky touch: HIT_R bumped 24→36 (≈44pt on iPhone, Apple HIG min).
// SNAP_R = 1.5× HIT_R defines the magnetic preview zone — when finger enters,
// the active drag line visually lerps toward the candidate vertex (snap feel).
// Commit still requires entering HIT_R proper (no accidental commits at edge).
const HIT_R = 36;
const SNAP_R = 54;
const STROKE_W = 3;

function ns<K extends keyof SVGElementTagNameMap>(name: K): SVGElementTagNameMap[K] {
  return document.createElementNS('http://www.w3.org/2000/svg', name);
}

export function mountPlay(host: HTMLElement, cfg: PlayConfig): PlayController {
  const { puzzle } = cfg;
  const startTime = performance.now();
  const traversed: number[] = []; // edge ids in order
  let head: number | null = null; // current vertex id
  let attempts = 0;
  let pointerActive = false;
  let pointerId: number | null = null;
  let dragX = 0;
  let dragY = 0;
  let won = false;
  let idleTimer1: ReturnType<typeof setTimeout> | null = null;
  let idleTimer2: ReturnType<typeof setTimeout> | null = null;
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  const accent = cfg.accentColor ?? rarityColor(puzzle.rarity);
  const hints = cfg.hints ?? false;
  const headHistory: number[] = []; // vertex ids visited, for undo-one
  let stuckToast: HTMLElement | null = null;
  let ruleToast: HTMLElement | null = null;
  let ruleToastTimer: ReturnType<typeof setTimeout> | null = null;
  let bubbleEl: SVGGElement | null = null;
  // v2.3 FDT-O-002 (I-19) — proactive "reachable next-dot" rings (hint A).
  const candidateRingEls = new Map<number, SVGCircleElement>();
  // Debounce for the bad-move explainer (hint B) so a drag sweeping over an
  // unconnected dot doesn't spam red connectors.
  let lastBadTarget = -1;
  let lastBadAt = -1e9;

  const svg = ns('svg');
  svg.setAttribute('viewBox', `0 0 ${VIEW} ${VIEW}`);
  svg.setAttribute(
    'aria-label',
    `Puzzle: ${puzzle.vertices.length} vertices, ${puzzle.edges.length} edges`,
  );
  svg.style.touchAction = 'none';
  svg.style.userSelect = 'none';
  svg.style.display = 'block';
  svg.style.width = '100%';
  svg.style.height = '100%';

  // P-2.1.3 — Global rotation fingerprint: ±3° per puzzle seed applied to root SVG.
  // Deterministic per seed, invisible to casual eye but unique per puzzle.
  if (!cfg.reduceMotion) {
    const rotDeg = (globalRotationFingerprint(puzzle.seed) * 180) / Math.PI;
    svg.style.transform = `rotate(${rotDeg.toFixed(2)}deg)`;
    svg.style.transformOrigin = 'center';
  }

  // Layout in pixels.
  const pad = 36;

  // M11 — Render Fingerprints: deterministic ±0.5px per-vertex jitter so each
  // puzzle looks subtly handcrafted. Hash the seed + vertex id for stability.
  function seedJitter(seed: string, vid: number, axis: number): number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    h = Math.imul(h ^ vid, 16777619);
    h = Math.imul(h ^ axis, 16777619);
    return ((h >>> 0) / 0xffffffff - 0.5) * 1.0; // ±0.5px
  }

  const coord = (v: Vertex): [number, number] => [
    pad + v.x * (VIEW - 2 * pad) + seedJitter(puzzle.seed, v.id, 0),
    pad + v.y * (VIEW - 2 * pad) + seedJitter(puzzle.seed, v.id, 1),
  ];

  // Build edge index for fast neighbor lookup.
  const edgesByVertex = new Map<number, Array<{ edgeId: number; other: number }>>();
  for (const e of puzzle.edges) {
    if (!edgesByVertex.has(e.a)) edgesByVertex.set(e.a, []);
    if (!edgesByVertex.has(e.b)) edgesByVertex.set(e.b, []);
    edgesByVertex.get(e.a)!.push({ edgeId: e.id, other: e.b });
    edgesByVertex.get(e.b)!.push({ edgeId: e.id, other: e.a });
  }

  // Render base layer.
  // gGlow sits below edges so start-vertex pulse doesn't obscure edge lines.
  const gGlow = ns('g');
  gGlow.classList.add('glow');
  const gEdges = ns('g');
  gEdges.classList.add('edges');
  const gVerts = ns('g');
  gVerts.classList.add('vertices');
  const gNums = ns('g');
  gNums.classList.add('step-nums');
  svg.appendChild(gGlow);
  svg.appendChild(gEdges);
  svg.appendChild(gVerts);
  svg.appendChild(gNums); // step-order numbers ride on top during slow replay

  const edgeEls = new Map<number, SVGLineElement>();
  for (const e of puzzle.edges) {
    const [ax, ay] = coord(puzzle.vertices[e.a]!);
    const [bx, by] = coord(puzzle.vertices[e.b]!);
    const line = ns('line');
    line.setAttribute('x1', String(ax));
    line.setAttribute('y1', String(ay));
    line.setAttribute('x2', String(bx));
    line.setAttribute('y2', String(by));
    line.setAttribute('stroke', 'var(--edge-rest, #CCC)');
    line.setAttribute('stroke-width', String(STROKE_W));
    line.setAttribute('stroke-linecap', 'round');
    line.setAttribute('data-edge', String(e.id));
    edgeEls.set(e.id, line);
    gEdges.appendChild(line);
  }

  // Active drag preview line.
  const activeLine = ns('line');
  activeLine.setAttribute('stroke', accent);
  activeLine.setAttribute('stroke-width', String(STROKE_W + 1));
  activeLine.setAttribute('stroke-linecap', 'round');
  activeLine.setAttribute('stroke-dasharray', '8 4');
  activeLine.setAttribute('opacity', '0');
  if (!cfg.reduceMotion) {
    activeLine.style.animation = 'fudeto-dash-flow 600ms linear infinite';
  }
  gEdges.appendChild(activeLine);

  /** Pulsing glow rings behind start/end vertices. Cleared on first touch. */
  const startGlowEls = new Map<number, SVGCircleElement>();

  /** Ghost hint lines and their cleanup timers. */
  let hintLines: SVGLineElement[] = [];
  let hintTimeouts: ReturnType<typeof setTimeout>[] = [];

  function clearHints(): void {
    for (const t of hintTimeouts) clearTimeout(t);
    hintTimeouts = [];
    for (const l of hintLines) l.remove();
    hintLines = [];
  }

  /**
   * Shows first 3 edges of puzzle.reference as translucent ghost lines,
   * staggered 200ms apart. They fade out after ~3s then remove themselves.
   * Only fires when no edges have been traversed yet.
   */
  function runHintAnimation(): void {
    if (traversed.length > 0 || !puzzle.reference || puzzle.reference.length === 0) return;
    clearHints();

    const toShow = puzzle.reference.slice(0, Math.min(3, puzzle.reference.length));
    for (let i = 0; i < toShow.length; i++) {
      const edgeId = toShow[i];
      const edge = puzzle.edges.find(e => e.id === edgeId);
      if (!edge) continue;
      const va = puzzle.vertices[edge.a];
      const vb = puzzle.vertices[edge.b];
      if (!va || !vb) continue;

      const [ax, ay] = coord(va);
      const [bx, by] = coord(vb);
      const ghost = ns('line');
      ghost.setAttribute('x1', String(ax));
      ghost.setAttribute('y1', String(ay));
      ghost.setAttribute('x2', String(bx));
      ghost.setAttribute('y2', String(by));
      ghost.setAttribute('stroke', '#C9A961');
      ghost.setAttribute('stroke-width', String(STROKE_W + 1));
      ghost.setAttribute('stroke-linecap', 'round');
      ghost.setAttribute('stroke-dasharray', '6 4');
      ghost.setAttribute('opacity', '0');
      ghost.style.pointerEvents = 'none';
      gEdges.insertBefore(ghost, gEdges.firstChild); // below traversed edges
      hintLines.push(ghost);

      // Staggered fade-in
      hintTimeouts.push(setTimeout(() => {
        ghost.style.transition = 'opacity 450ms ease';
        ghost.setAttribute('opacity', '0.50');
      }, i * 200));
    }

    // Hold for 2.5s then fade out
    const holdMs = toShow.length * 200 + 2500;
    hintTimeouts.push(setTimeout(() => {
      for (const l of hintLines) {
        l.style.transition = 'opacity 600ms ease';
        l.setAttribute('opacity', '0');
      }
      hintTimeouts.push(setTimeout(clearHints, 650));
    }, holdMs));
  }

  const vertEls = new Map<number, SVGCircleElement>();
  const hitEls = new Map<number, SVGCircleElement>();
  for (const v of puzzle.vertices) {
    const [x, y] = coord(v);
    const c = ns('circle');
    c.setAttribute('cx', String(x));
    c.setAttribute('cy', String(y));
    c.setAttribute('r', String(VERT_R));
    c.setAttribute('fill', 'var(--ink, #1A1A1A)');
    c.setAttribute('data-v', String(v.id));
    vertEls.set(v.id, c);
    gVerts.appendChild(c);

    const hit = ns('circle');
    hit.setAttribute('cx', String(x));
    hit.setAttribute('cy', String(y));
    hit.setAttribute('r', String(HIT_R));
    hit.setAttribute('fill', 'transparent');
    hit.setAttribute('data-v-hit', String(v.id));
    hit.style.cursor = 'pointer';
    hitEls.set(v.id, hit);
    gVerts.appendChild(hit);
  }

  // Pulse on start-eligible vertices (odd-degree ones, or all if 0 odd).
  highlightStartVertices();

  // Status ring around head.
  const headRing = ns('circle');
  headRing.setAttribute('r', String(VERT_R + 4));
  headRing.setAttribute('fill', 'none');
  headRing.setAttribute('stroke', accent);
  headRing.setAttribute('stroke-width', '2');
  headRing.setAttribute('opacity', '0');
  gVerts.appendChild(headRing);

  // Wire input.
  svg.addEventListener('pointerdown', onPointerDown);
  svg.addEventListener('pointermove', onPointerMove);
  svg.addEventListener('pointerup', onPointerUp);
  svg.addEventListener('pointercancel', onPointerUp);
  svg.addEventListener('contextmenu', (ev) => ev.preventDefault());

  host.appendChild(svg);

  ensureKeyframes();
  resetIdle();

  // v2.3 FDT-O-002 — one-time first-solve bubble (only if hints on, not yet shown,
  // and the player hasn't already started). Points at the pulsing start vertex.
  if (hints && cfg.firstHintBubble) {
    setTimeout(() => {
      if (head === null && !won) showFirstHintBubble();
    }, 600);
  }

  function highlightStartVertices(): void {
    // Clear previous rings before re-highlighting (e.g., after reset).
    for (const [, g] of startGlowEls) g.remove();
    startGlowEls.clear();

    const degree = new Map<number, number>();
    for (const e of puzzle.edges) {
      degree.set(e.a, (degree.get(e.a) ?? 0) + 1);
      degree.set(e.b, (degree.get(e.b) ?? 0) + 1);
    }
    const odds: number[] = [];
    for (const [v, d] of degree) if (d % 2 === 1) odds.push(v);
    const eligible = odds.length > 0 ? odds : Array.from(degree.keys());

    // Euler path: 2 odd-degree vertices — start = pulsing accent ring, end = static accent ring
    // Euler circuit: all even-degree — only start vertex gets a pulsing ring
    const [startV, endV] = odds.length === 2 ? [odds[0], odds[1]] : [eligible[0], undefined];

    // Vertex circles stay at default ink fill — no gold/silver stroke per Claude Design spec.
    // Rings are drawn as separate <circle> elements in gGlow (below edges) so they don't
    // interfere with hit-testing.
    const ringVertices: Array<{ id: number; pulse: boolean }> = [
      { id: startV, pulse: true },
      ...(endV !== undefined ? [{ id: endV, pulse: false }] : []),
    ];

    for (const { id: v, pulse } of ringVertices) {
      const vert = puzzle.vertices[v];
      if (!vert) continue;
      const [gx, gy] = coord(vert);

      const ring = ns('circle');
      ring.setAttribute('cx', String(gx));
      ring.setAttribute('cy', String(gy));
      ring.setAttribute('r', String(VERT_R + 5));
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', accent);
      ring.setAttribute('stroke-width', '2');
      ring.style.pointerEvents = 'none';

      if (pulse && !cfg.reduceMotion) {
        // Start vertex: pulsing ring — matches Claude Design ring-start spec
        // (transform-box + transform-origin needed for SVG scale-from-center)
        ring.style.transformBox = 'fill-box';
        ring.style.transformOrigin = 'center';
        ring.style.animation = 'fudeto-vertex-pulse 1200ms ease-in-out infinite';
      } else {
        // End vertex (or reduceMotion): static ring at lower opacity
        ring.setAttribute('opacity', '0.45');
      }

      gGlow.appendChild(ring);
      startGlowEls.set(v, ring);
    }
  }

  /** Fade and remove start/end rings on first pointer interaction. */
  let startEndFaded = false;
  function fadeStartEndOnFirstTouch(): void {
    if (startEndFaded) return;
    startEndFaded = true;
    // Fade and remove the rarity-color rings from gGlow.
    for (const [, ring] of startGlowEls) {
      if (!cfg.reduceMotion) {
        ring.style.transition = 'opacity 250ms ease';
        ring.style.opacity = '0';
        setTimeout(() => ring.remove(), 280);
      } else {
        ring.remove();
      }
    }
    startGlowEls.clear();
    // Clear any active hint animation.
    clearHints();
  }

  /** v2.3 FDT-O-002 — shake + a brief austere dim to flag a dead-end / already-drawn
   *  move (non-blocking). I-20: NO red — the shake carries the "no", the faint ink
   *  dim is the secondary cue, all within the single-gold + ink discipline. */
  function shakeEdge(edgeId: number): void {
    const line = edgeEls.get(edgeId);
    if (!line) return;
    const prevStroke = line.getAttribute('stroke') ?? accent;
    line.setAttribute('stroke', 'var(--color-ink-soft, rgba(245,241,232,0.55))');
    if (!cfg.reduceMotion) {
      line.style.animation = 'fudeto-edge-shake 360ms ease';
      setTimeout(() => {
        line.style.animation = '';
      }, 380);
    }
    setTimeout(() => {
      line.style.transition = 'stroke 300ms ease';
      line.setAttribute('stroke', prevStroke);
    }, 600);
  }

  /** Shared austere toast: <button> when clickable (undo), else a transient <div>. */
  function mkToast(cls: string, text: string, onClick?: () => void): HTMLElement {
    const el = document.createElement(onClick ? 'button' : 'div');
    el.className = cls;
    el.textContent = text;
    el.setAttribute('aria-live', 'polite');
    if (onClick) {
      (el as HTMLButtonElement).type = 'button';
      el.onclick = (e) => { e.preventDefault(); onClick(); };
    }
    host.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-in'));
    return el;
  }
  function rmToast(t: HTMLElement | null): void {
    if (!t) return;
    t.classList.remove('is-in');
    setTimeout(() => t.remove(), 260);
  }

  /** v2.3 FDT-O-002 — bottom toast offering undo when the player paints into a corner. */
  function showStuckToast(): void {
    if (stuckToast || !cfg.hintLabels) return;
    stuckToast = mkToast('play-stuck-toast', cfg.hintLabels.stuck, () => {
      undoLast();
      hideStuckToast();
    });
  }

  function hideStuckToast(): void {
    rmToast(stuckToast);
    stuckToast = null;
  }

  /**
   * Transient rule reminder ("each line can only be drawn once"). Informational —
   * auto-fades, non-clickable, never blocks input. Honours I-19 (hints only).
   * Re-showing resets the timer so rapid repeats don't stack toasts.
   */
  function showRuleToast(message: string): void {
    if (!cfg.hintLabels) return;
    if (!ruleToast) ruleToast = mkToast('play-rule-toast', message);
    if (ruleToastTimer) clearTimeout(ruleToastTimer);
    ruleToastTimer = setTimeout(hideRuleToast, 1600);
  }

  function hideRuleToast(): void {
    if (ruleToastTimer) { clearTimeout(ruleToastTimer); ruleToastTimer = null; }
    rmToast(ruleToast);
    ruleToast = null;
  }

  /** Undo the most recent committed edge (restores head + edge color). */
  function undoLast(): void {
    const last = traversed.pop();
    if (last === undefined) return;
    const line = edgeEls.get(last);
    if (line) {
      line.style.animation = '';
      line.style.transition = 'stroke 200ms ease';
      line.setAttribute('stroke', 'var(--edge-rest, #888)');
    }
    head = headHistory.pop() ?? head;
    updateHeadRing();
    updateCandidateRings();
    updateVertexExhaustion();
    cfg.onProgress?.(traversed.length, puzzle.edges.length);
  }

  /** v2.3 FDT-O-002 — one-time first-solve bubble pointing at the pulsing start vertex. */
  function showFirstHintBubble(): void {
    if (!cfg.firstHintBubble || !cfg.hintLabels || bubbleEl) return;
    const degree = new Map<number, number>();
    for (const e of puzzle.edges) {
      degree.set(e.a, (degree.get(e.a) ?? 0) + 1);
      degree.set(e.b, (degree.get(e.b) ?? 0) + 1);
    }
    const odds: number[] = [];
    for (const [v, d] of degree) if (d % 2 === 1) odds.push(v);
    const startV = odds.length === 2 ? odds[0]! : (puzzle.vertices[0]?.id ?? 0);
    const sv = puzzle.vertices[startV];
    if (!sv) return;
    const [gx, gy] = coord(sv);

    const label = cfg.hintLabels.startBubble;
    const w = Math.min(VIEW - 16, label.length * 12 + 24);
    const h = 22;
    const bx = Math.max(8, Math.min(VIEW - w - 8, gx - w / 2));
    const by = Math.max(8, gy - VERT_R - h - 12);

    const g = ns('g');
    g.style.pointerEvents = 'none';
    const rect = ns('rect');
    rect.setAttribute('x', String(bx));
    rect.setAttribute('y', String(by));
    rect.setAttribute('width', String(w));
    rect.setAttribute('height', String(h));
    rect.setAttribute('rx', '11');
    rect.setAttribute('fill', 'var(--color-gold, #C9A961)');
    const txt = ns('text');
    txt.setAttribute('x', String(bx + w / 2));
    txt.setAttribute('y', String(by + h / 2 + 4));
    txt.setAttribute('text-anchor', 'middle');
    txt.setAttribute('font-size', '11');
    txt.setAttribute('fill', '#1A1A1A');
    txt.textContent = label;
    g.appendChild(rect);
    g.appendChild(txt);
    if (!cfg.reduceMotion) {
      g.style.transformOrigin = `${gx}px ${by}px`;
      g.style.animation = 'fudeto-bubble-in 320ms ease-out';
    }
    svg.appendChild(g);
    bubbleEl = g;
    cfg.onHintBubbleShown?.();
    setTimeout(() => dismissHintBubble(), 5000);
  }

  function dismissHintBubble(): void {
    if (!bubbleEl) return;
    const b = bubbleEl;
    bubbleEl = null;
    b.style.transition = 'opacity 220ms ease';
    b.style.opacity = '0';
    setTimeout(() => b.remove(), 240);
  }

  function svgPoint(ev: PointerEvent): [number, number] {
    // Use the SVG's inverse screen-CTM so the conversion respects the per-puzzle
    // global rotation fingerprint (±3°, applied above) — the previous axis-aligned
    // `(clientX - rect.left)/rect.width * VIEW` ignored that rotation, so taps on
    // every (rotated) puzzle missed vertices by 30-50 SVG units (HIT_R=36) and
    // read as dead clicks. Real production bug (session 881). Falls back to the
    // old formula only if getScreenCTM is unavailable (defensive).
    const ctm = svg.getScreenCTM();
    if (ctm) {
      const inv = ctm.inverse();
      return [
        inv.a * ev.clientX + inv.c * ev.clientY + inv.e,
        inv.b * ev.clientX + inv.d * ev.clientY + inv.f,
      ];
    }
    const rect = svg.getBoundingClientRect();
    return [
      ((ev.clientX - rect.left) / rect.width) * VIEW,
      ((ev.clientY - rect.top) / rect.height) * VIEW,
    ];
  }

  function findVertexAt(x: number, y: number, radius = HIT_R): number | null {
    for (const v of puzzle.vertices) {
      const [vx, vy] = coord(v);
      const dx = vx - x;
      const dy = vy - y;
      if (dx * dx + dy * dy <= radius * radius) return v.id;
    }
    return null;
  }

  /**
   * v2.2 P-3.1 — Magnetic snap target lookup.
   * Returns the nearest CANDIDATE vertex (reachable from head via unused edge)
   * within `radius`. Used by updateActiveLine to lerp visual preview toward it.
   * Returns null if head is unset or no candidate within radius.
   */
  function findCandidateAt(x: number, y: number, radius: number): number | null {
    if (head === null) return null;
    const neighbors = edgesByVertex.get(head) ?? [];
    let bestId: number | null = null;
    let bestDist = radius * radius;
    for (const n of neighbors) {
      if (traversed.includes(n.edgeId)) continue;
      const v = puzzle.vertices[n.other];
      if (!v) continue;
      const [vx, vy] = coord(v);
      const dx = vx - x;
      const dy = vy - y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= bestDist) {
        bestDist = d2;
        bestId = n.other;
      }
    }
    return bestId;
  }

  function eligibleStart(v: number): boolean {
    if (traversed.length > 0) return false;
    const degree = new Map<number, number>();
    for (const e of puzzle.edges) {
      degree.set(e.a, (degree.get(e.a) ?? 0) + 1);
      degree.set(e.b, (degree.get(e.b) ?? 0) + 1);
    }
    let oddCount = 0;
    for (const d of degree.values()) if (d % 2 === 1) oddCount++;
    if (oddCount === 0) return true; // any vertex
    return (degree.get(v) ?? 0) % 2 === 1;
  }

  function findEdgeBetween(a: number, b: number): number | null {
    const list = edgesByVertex.get(a) ?? [];
    for (const item of list) {
      if (item.other === b && !traversed.includes(item.edgeId)) return item.edgeId;
    }
    return null;
  }

  function onPointerDown(ev: PointerEvent): void {
    if (won) return;
    ev.preventDefault();
    // Immediately dismiss any active hint animation on touch.
    clearHints();
    dismissHintBubble();
    hideStuckToast();
    const [x, y] = svgPoint(ev);
    const v = findVertexAt(x, y);
    if (v == null) return;
    svg.setPointerCapture(ev.pointerId);
    pointerId = ev.pointerId;
    pointerActive = true;
    dragX = x;
    dragY = y;

    // M10 — fade start/end outlines on first valid touch
    fadeStartEndOnFirstTouch();

    if (head === null) {
      if (!eligibleStart(v)) {
        flashVertex(v);
        pointerActive = false;
        return;
      }
      head = v;
      updateHeadRing();
      updateCandidateRings();
      schedulingLongPress(v);
    } else if (v === head) {
      schedulingLongPress(v);
    } else {
      // Tap on a non-head vertex — try commit edge from head.
      tryAdvanceTo(v);
    }
    showActiveLine();
    resetIdle();
  }

  function onPointerMove(ev: PointerEvent): void {
    if (!pointerActive || won || pointerId !== ev.pointerId) return;
    const [x, y] = svgPoint(ev);
    dragX = x;
    dragY = y;
    cancelLongPress();
    if (head !== null) {
      // If we crossed into a new vertex hit area, attempt to advance. Drag-sweep
      // grazing never counts as a mistake (countMistake=false) — only a valid
      // edge advances; incidental passes over non-adjacent / spent vertices are
      // silent, so a clean continuous stroke = 0 mistakes = 3★.
      const v = findVertexAt(x, y, HIT_R);
      if (v != null && v !== head) tryAdvanceTo(v, false);
    }
    updateActiveLine();
    resetIdle();
  }

  function onPointerUp(ev: PointerEvent): void {
    if (pointerId !== ev.pointerId) return;
    if (svg.hasPointerCapture(ev.pointerId)) svg.releasePointerCapture(ev.pointerId);
    pointerActive = false;
    pointerId = null;
    cancelLongPress();
    hideActiveLine();
  }

  // `countMistake=false` for drag-sweep grazing: a continuous one-stroke naturally
  // passes through / re-enters vertices (Euler trails revisit nodes), and those
  // incidental grazes are NOT deliberate wrong moves — counting them inflated the
  // mistake tally so even an optimal stroke scored 1★. Only explicit taps count.
  function tryAdvanceTo(target: number, countMistake = true): void {
    if (head === null) return;
    const edgeId = findEdgeBetween(head, target);
    if (edgeId == null) {
      if (countMistake) {
        attempts++;
        // Hint B (toggleable): explain the rejection. Purist mode keeps the silent flash.
        if (hints) explainBadMove(target);
        else flashVertex(target);
      }
      return;
    }
    commitEdge(edgeId, target);
  }

  function commitEdge(edgeId: number, target: number): void {
    if (head !== null) headHistory.push(head);
    traversed.push(edgeId);
    head = target;
    dismissHintBubble();
    const line = edgeEls.get(edgeId);
    if (line) {
      line.setAttribute('stroke', accent);
      line.style.transition = 'stroke 180ms ease';
    }
    updateHeadRing();
    cfg.onProgress?.(traversed.length, puzzle.edges.length);
    if (traversed.length === puzzle.edges.length) {
      win();
      return;
    }
    // Hint A: refresh which dots are reachable from the new head + dim spent dots.
    updateCandidateRings();
    updateVertexExhaustion();
    // v2.3 FDT-O-002 — rule-hint feedback (I-19 toggleable; never blocks input).
    //   stuck (terminal): head has no untraversed incident edge → undo toast.
    //   dead-end (predictive): this move strands the remaining graph → shake red.
    if (hints) {
      if (!headHasMove()) {
        showStuckToast();
      } else {
        hideStuckToast();
        if (!remainingCanComplete()) shakeEdge(edgeId);
      }
    }
  }

  /** True if head still has at least one untraversed incident edge. */
  function headHasMove(): boolean {
    if (head === null) return true;
    for (const n of edgesByVertex.get(head) ?? []) {
      if (!traversed.includes(n.edgeId)) return true;
    }
    return false;
  }

  /**
   * Euler-trail feasibility of the REMAINING (untraversed) edges, starting from
   * head. True iff: head has a remaining incident edge, all remaining edges are
   * reachable from head, and the remaining subgraph has 0 or 2 odd-degree
   * vertices (and if 2, head is one of them). O(V+E) — fine for ≤20 vertices.
   */
  function remainingCanComplete(): boolean {
    if (head === null) return true;
    return canCompleteEulerTrail(puzzle.edges, new Set(traversed), head);
  }

  /**
   * v2.3 FDT-O-005 (moment 3) — "Admire": redraw the solved stroke in the order
   * it was traced, a silent ~1.1s celebration. Each edge sweeps in via
   * stroke-dashoffset. reduce-motion → no-op (edges stay solid from win()'s glow).
   */
  // FDT-O-005/009 — sweep-draw a set of edges (in order) via stroke-dashoffset.
  // Shared by replaySolvedPath (win "Admire" — the user's traced order) and the
  // controller's playSolution (review screen — the canonical reference trail).
  /**
   * Animate a trail drawing itself out, edge by edge.
   * - default (post-win "admire"): brisk, finishes well inside the ~1.3s window
   *   before the result screen takes over.
   * - slow (review "watch the solution"): the player is just watching, so go
   *   slower with a long per-edge overlap → the segments blend into one
   *   continuous, silky flowing stroke instead of discrete ticks.
   */
  function sweepEdges(order: number[], opts: { slow?: boolean; force?: boolean } = {}): void {
    if ((cfg.reduceMotion && !opts.force) || order.length === 0) return;
    const slow = opts.slow === true;
    // Stagger between consecutive edge starts.
    const per = slow
      ? Math.max(150, Math.min(300, Math.round(2600 / order.length)))
      : Math.max(70, Math.min(150, Math.round(1100 / order.length)));
    // Draw duration per edge. Slow mode overlaps far past `per` so the stroke
    // reads as one flowing line; a gentle ease keeps both ends soft (silky).
    const drawMs = slow ? per + 320 : per + 60;
    const ease = slow ? 'cubic-bezier(0.45, 0.05, 0.2, 1)' : 'cubic-bezier(0.4,0,0.2,1)';
    order.forEach((edgeId, i) => {
      const line = edgeEls.get(edgeId);
      if (!line) return;
      const len =
        Math.hypot(
          Number(line.getAttribute('x2')) - Number(line.getAttribute('x1')),
          Number(line.getAttribute('y2')) - Number(line.getAttribute('y1')),
        ) || 1;
      // Hide instantly (same tick → no flicker), set accent, then sweep in.
      line.setAttribute('stroke', accent);
      line.style.transition = 'none';
      line.style.strokeDasharray = String(len);
      line.style.strokeDashoffset = String(len);
      setTimeout(() => {
        line.style.transition = `stroke-dashoffset ${drawMs}ms ${ease}`;
        line.style.strokeDashoffset = '0';
      }, i * per);
    });
    setTimeout(() => {
      for (const id of order) {
        const l = edgeEls.get(id);
        if (!l) continue;
        l.style.strokeDasharray = '';
        l.style.strokeDashoffset = '';
        l.style.transition = '';
      }
    }, order.length * per + drawMs + 120);
  }

  /** Review replay as ONE continuous pen stroke: edges drawn in order, each
   *  revealing from the connecting vertex toward the next (not independent
   *  segments flashing in). Falls back to the segment sweep if the trail can't
   *  be reconstructed as a connected walk. */
  function traceStroke(valid: number[], force: boolean, slow = true): void {
    if (cfg.reduceMotion && !force) return; // edges already solid; skip the redraw
    const seq = vertexSeqFromEdges(puzzle.edges, valid);
    if (!seq || seq.length !== valid.length + 1) {
      sweepEdges(valid, { slow, force });
      return;
    }
    const byId = new Map(puzzle.edges.map((e) => [e.id, e] as const));
    // slow (review "watch"): silky ~2.6-3.2s. fast (post-win admire): brisk so it
    // finishes inside the ~1.3s before the result screen takes over.
    const drawMs = slow
      ? Math.max(170, Math.min(380, Math.round(3200 / valid.length)))
      : Math.max(60, Math.min(160, Math.round(1100 / valid.length)));
    const per = Math.round(drawMs * 0.9); // tiny overlap at joints → no stutter
    valid.forEach((edgeId, i) => {
      const line = edgeEls.get(edgeId);
      const e = byId.get(edgeId);
      if (!line || !e) return;
      const len =
        Math.hypot(
          Number(line.getAttribute('x2')) - Number(line.getAttribute('x1')),
          Number(line.getAttribute('y2')) - Number(line.getAttribute('y1')),
        ) || 1;
      line.setAttribute('stroke', accent);
      line.style.transition = 'none';
      line.style.strokeDasharray = String(len);
      // reveal in traversal direction: from===a → x1→x2 (+len); from===b → x2→x1 (−len)
      line.style.strokeDashoffset = String(seq[i] === e.a ? len : -len);
      setTimeout(() => {
        line.style.transition = `stroke-dashoffset ${drawMs}ms linear`;
        line.style.strokeDashoffset = '0';
      }, i * per);
    });
    // Pen-head step numbers (slow review only — keeps the fast admire clean): a
    // small number fades in at each vertex AS the stroke reaches it, then fades
    // out. Shows the linear visit order (1,2,3…) and handles Euler revisits (each
    // pass gets its own transient number) without persistent clutter.
    if (slow) {
      gNums.replaceChildren();
      seq.forEach((vid, k) => {
        const v = puzzle.vertices[vid];
        if (!v) return;
        const [x, y] = coord(v);
        const txt = ns('text');
        txt.setAttribute('x', String(x + 13));
        txt.setAttribute('y', String(y - 11));
        txt.setAttribute('class', 'step-num');
        txt.textContent = String(k + 1);
        gNums.appendChild(txt);
        const at = k === 0 ? 0 : (k - 1) * per + Math.round(drawMs * 0.85);
        setTimeout(() => txt.classList.add('is-on'), at);
        setTimeout(() => txt.classList.remove('is-on'), at + 900);
        setTimeout(() => txt.remove(), at + 1300);
      });
    }
    setTimeout(
      () => {
        for (const id of valid) {
          const l = edgeEls.get(id);
          if (!l) continue;
          l.style.strokeDasharray = '';
          l.style.strokeDashoffset = '';
          l.style.transition = '';
        }
      },
      valid.length * per + drawMs + 120,
    );
  }

  function replaySolvedPath(): void {
    // Post-win "admire": redraw the solve as ONE continuous pen stroke in the
    // actual traversal order + direction (fast, so it finishes before the result
    // screen). Was a per-edge sweep that revealed each segment in its own x1→x2
    // direction — so it read as points wiring up out of order, with no sense of a
    // single timed stroke. Falls back to the segment sweep only if the recorded
    // trail can't be reconstructed as a connected walk.
    traceStroke(traversed.slice(), false, false);
  }

  function win(): void {
    won = true;
    cancelLongPress();
    stopIdle();
    clearCandidateRings();
    const timeMs = Math.round(performance.now() - startTime);
    // Soft completion glow on all edges (also the reduce-motion final state).
    for (const e of edgeEls.values()) {
      e.setAttribute('stroke', accent);
      e.style.opacity = '1';
    }
    // FDT-O-005 moment 3 — admire the solved stroke (silent redraw; reduce-motion no-op).
    const solvedPath = traversed.slice();
    replaySolvedPath();
    cfg.onWin?.({ timeMs, attempts, path: solvedPath });
  }

  function updateActiveLine(): void {
    if (head === null) {
      activeLine.setAttribute('opacity', '0');
      return;
    }
    const [hx, hy] = coord(puzzle.vertices[head]!);

    // v2.2 P-3.1 — Magnetic snap: when drag inside SNAP_R of a candidate vertex,
    // visually lerp the preview line endpoint toward that vertex. Lerp factor t:
    //   dist >= SNAP_R → t=0 (no snap, follow finger)
    //   dist <= HIT_R  → t=1 (fully attached to vertex)
    // Between → smooth blend so user FEELS the pull as they approach.
    let endX = dragX;
    let endY = dragY;
    const snap = findCandidateAt(dragX, dragY, SNAP_R);
    if (snap !== null) {
      const sv = puzzle.vertices[snap];
      if (sv) {
        const [vx, vy] = coord(sv);
        const dx = vx - dragX;
        const dy = vy - dragY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const t = Math.max(0, Math.min(1, (SNAP_R - dist) / Math.max(1, SNAP_R - HIT_R)));
        endX = dragX + dx * t;
        endY = dragY + dy * t;
      }
    }

    activeLine.setAttribute('x1', String(hx));
    activeLine.setAttribute('y1', String(hy));
    activeLine.setAttribute('x2', String(endX));
    activeLine.setAttribute('y2', String(endY));
    activeLine.setAttribute('opacity', '1');
  }

  function showActiveLine(): void {
    updateActiveLine();
  }
  function hideActiveLine(): void {
    activeLine.setAttribute('opacity', '0');
  }

  /**
   * Hint A (I-19, toggleable) — ring every dot you can legally move to from the
   * current head (an incident, not-yet-drawn edge). Pure visual, single accent
   * color + ring SHAPE (UI-3 color+shape pairing), sits in gGlow below edges so
   * it never blocks hit-testing. Cleared + recomputed on every head change.
   */
  function clearCandidateRings(): void {
    for (const [, r] of candidateRingEls) r.remove();
    candidateRingEls.clear();
  }
  function updateCandidateRings(): void {
    clearCandidateRings();
    if (!hints || won || head === null) return;
    const seen = new Set<number>();
    for (const n of edgesByVertex.get(head) ?? []) {
      if (traversed.includes(n.edgeId) || seen.has(n.other)) continue;
      seen.add(n.other);
      const v = puzzle.vertices[n.other];
      if (!v) continue;
      const [cx, cy] = coord(v);
      const ring = ns('circle');
      ring.setAttribute('cx', String(cx));
      ring.setAttribute('cy', String(cy));
      ring.setAttribute('r', String(VERT_R + 4));
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', accent);
      ring.setAttribute('stroke-width', '2');
      ring.setAttribute('opacity', '0');
      ring.style.pointerEvents = 'none';
      gGlow.appendChild(ring);
      candidateRingEls.set(n.other, ring);
      if (!cfg.reduceMotion) {
        ring.style.transition = 'opacity 200ms ease';
        requestAnimationFrame(() => ring.setAttribute('opacity', '0.32'));
      } else {
        ring.setAttribute('opacity', '0.32');
      }
    }
  }

  /** Is there ANY edge (drawn or not) between a and b? */
  function edgeBetweenAny(a: number, b: number): { edgeId: number; used: boolean } | null {
    for (const item of edgesByVertex.get(a) ?? []) {
      if (item.other === b) return { edgeId: item.edgeId, used: traversed.includes(item.edgeId) };
    }
    return null;
  }

  /**
   * Hint B (I-19, toggleable) — explain WHY a tapped connection is illegal, at a
   * glance, no text:
   *   - edge exists but already drawn → red-shake that existing line.
   *   - no edge at all → flash a transient red dashed connector head→target.
   * Debounced per-target so a drag-sweep doesn't spam.
   */
  function explainBadMove(target: number): void {
    if (head === null) return;
    const now = performance.now();
    if (target === lastBadTarget && now - lastBadAt < 600) return;
    lastBadTarget = target;
    lastBadAt = now;
    const any = edgeBetweenAny(head, target);
    if (any && any.used) {
      shakeEdge(any.edgeId); // "you already drew this line"
      if (cfg.hintLabels) showRuleToast(cfg.hintLabels.noRepeat); // I-19: hints only
    } else {
      flashBadConnector(target); // "there's no line between these two dots"
    }
    flashVertex(target);
  }

  function flashBadConnector(target: number): void {
    if (head === null) return;
    const [hx, hy] = coord(puzzle.vertices[head]!);
    const [tx, ty] = coord(puzzle.vertices[target]!);
    const ln = ns('line');
    ln.setAttribute('x1', String(hx));
    ln.setAttribute('y1', String(hy));
    ln.setAttribute('x2', String(tx));
    ln.setAttribute('y2', String(ty));
    ln.setAttribute('stroke', 'var(--color-error, #f87171)');
    ln.setAttribute('stroke-width', String(STROKE_W));
    ln.setAttribute('stroke-linecap', 'round');
    ln.setAttribute('stroke-dasharray', '3 5');
    ln.setAttribute('opacity', '0.9');
    ln.style.pointerEvents = 'none';
    gEdges.appendChild(ln);
    setTimeout(() => {
      ln.style.transition = 'opacity 400ms ease';
      ln.setAttribute('opacity', '0');
      setTimeout(() => ln.remove(), 420);
    }, 260);
  }

  function updateHeadRing(): void {
    if (head === null) {
      headRing.setAttribute('opacity', '0');
      return;
    }
    const [x, y] = coord(puzzle.vertices[head]!);
    headRing.setAttribute('cx', String(x));
    headRing.setAttribute('cy', String(y));
    headRing.setAttribute('opacity', '1');
    // mark the head vertex with accent
    for (const [vid, c] of vertEls) {
      c.setAttribute('fill', vid === head ? accent : 'var(--ink, #F5F1E8)');
    }
  }

  /**
   * Hint A-cont (I-19, toggleable) — teach "a dot can be re-entered" without
   * text: a vertex stays solid as long as it still has an undrawn incident edge
   * (i.e. you can still come back to it). Once ALL its lines are drawn it can
   * never be revisited, so it dims to a "spent" state. Reuses the existing
   * opacity-fade visual language (flashVertex / start-end fade) — no new color,
   * no shape invention (I-20). Head always stays solid (you are there now).
   */
  function updateVertexExhaustion(): void {
    if (!hints) return;
    for (const [vid, c] of vertEls) {
      if (vid === head) {
        c.style.opacity = '1';
        continue;
      }
      let hasUnused = false;
      for (const n of edgesByVertex.get(vid) ?? []) {
        if (!traversed.includes(n.edgeId)) {
          hasUnused = true;
          break;
        }
      }
      if (!cfg.reduceMotion) c.style.transition = 'opacity 220ms ease';
      c.style.opacity = hasUnused ? '1' : '0.3';
    }
  }

  function flashVertex(v: number): void {
    const c = vertEls.get(v);
    if (!c) return;
    c.style.transition = 'transform 180ms ease, opacity 180ms ease';
    c.style.opacity = '0.4';
    setTimeout(() => {
      c.style.opacity = '1';
    }, 200);
  }

  function schedulingLongPress(v: number): void {
    cancelLongPress();
    longPressTimer = setTimeout(() => {
      if (head !== null && traversed.length === 0) {
        head = v;
        updateHeadRing();
      } else if (traversed.length === 0) {
        head = v;
        updateHeadRing();
      } else {
        // Reset path entirely.
        controller.reset();
      }
    }, 500);
  }

  function cancelLongPress(): void {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function resetIdle(): void {
    clearHints();
    if (idleTimer1) clearTimeout(idleTimer1);
    if (idleTimer2) clearTimeout(idleTimer2);
    // 3s idle → wiggle signal + internal hint animation (first 3 path segments).
    // 8s idle → 'hint' signal for host screen (reserved for stronger nudge).
    idleTimer1 = setTimeout(() => {
      cfg.onIdle?.('wiggle');
      if (hints) runHintAnimation(); // I-19: ghost-path is a hint, gated by toggle
    }, 3000);
    idleTimer2 = setTimeout(() => cfg.onIdle?.('hint'), 8000);
  }
  function stopIdle(): void {
    clearHints();
    if (idleTimer1) clearTimeout(idleTimer1);
    if (idleTimer2) clearTimeout(idleTimer2);
    idleTimer1 = null;
    idleTimer2 = null;
  }

  const controller: PlayController = {
    el: svg,
    destroy() {
      cancelLongPress();
      stopIdle();
      clearHints();
      clearCandidateRings();
      dismissHintBubble();
      hideStuckToast();
      hideRuleToast();
      svg.remove();
    },
    undo() {
      undoLast();
    },
    reset() {
      clearHints();
      dismissHintBubble();
      hideStuckToast();
      hideRuleToast();
      traversed.length = 0;
      headHistory.length = 0;
      head = null;
      attempts = 0;
      won = false;
      startEndFaded = false;
      for (const [, l] of edgeEls) {
        l.setAttribute('stroke', 'var(--edge-rest, #888)');
        l.style.opacity = '1';
      }
      for (const [, c] of vertEls) {
        c.setAttribute('fill', 'var(--ink, #F5F1E8)');
        c.removeAttribute('stroke');
        c.style.strokeOpacity = '';
        c.style.opacity = '1'; // clear any "spent" dimming from the prior attempt
      }
      highlightStartVertices(); // re-adds glow rings for fresh start
      clearCandidateRings();
      headRing.setAttribute('opacity', '0');
      activeLine.setAttribute('opacity', '0');
      cfg.onProgress?.(0, puzzle.edges.length);
      resetIdle();
    },
    elapsed() {
      return performance.now() - startTime;
    },
    playSolution(force = false) {
      const ref = puzzle.reference ?? [];
      if ((cfg.reduceMotion && !force) || ref.length === 0) {
        // Static (reduce-motion w/o force, or no reference): show all at once.
        for (const e of edgeEls.values()) {
          e.setAttribute('stroke', accent);
          e.style.opacity = '1';
        }
        return;
      }
      // Review playback: ONE continuous pen stroke, start → end.
      traceStroke([...ref], force);
    },
    playPath(order: number[], force = false) {
      const valid = (order ?? []).filter((id) => edgeEls.has(id));
      if ((cfg.reduceMotion && !force) || valid.length === 0) {
        for (const e of edgeEls.values()) {
          e.setAttribute('stroke', accent);
          e.style.opacity = '1';
        }
        return;
      }
      traceStroke(valid, force);
    },
    _state() {
      return { traversed: traversed.slice(), head };
    },
  };
  return controller;
}

function rarityColor(r: Edge[] extends never ? never : Puzzle['rarity']): string {
  switch (r) {
    case 'common':
      return 'var(--rarity-common, #4ade80)';
    case 'uncommon':
      return 'var(--rarity-uncommon, #60a5fa)';
    case 'rare':
      return 'var(--rarity-rare, #f87171)';
    case 'epic':
      return 'var(--rarity-epic, #c084fc)';
    case 'legendary':
      return 'var(--rarity-legendary, #c9a961)';
  }
}

/**
 * v2.3 FDT-O-002 — Euler-trail feasibility of the UNTRAVERSED edges, starting
 * from `head`. Pure + exported for testability (dead-end hint correctness).
 *
 * Returns true iff the remaining edges can still be drawn as one continuous
 * stroke from head: head has a remaining incident edge, all remaining edges are
 * reachable from head (connectivity), and the remaining subgraph has 0 or 2
 * odd-degree vertices (and if 2, head must be one of them). O(V+E).
 *
 * `traversed` is the set of already-drawn edge ids. Empty remaining → true.
 */
export function canCompleteEulerTrail(
  edges: ReadonlyArray<{ id: number; a: number; b: number }>,
  traversed: ReadonlySet<number>,
  head: number,
): boolean {
  const remaining = edges.filter((e) => !traversed.has(e.id));
  if (remaining.length === 0) return true;
  const adj = new Map<number, number[]>();
  const deg = new Map<number, number>();
  const push = (v: number, other: number) => {
    const list = adj.get(v);
    if (list) list.push(other);
    else adj.set(v, [other]);
    deg.set(v, (deg.get(v) ?? 0) + 1);
  };
  for (const e of remaining) {
    push(e.a, e.b);
    push(e.b, e.a);
  }
  if (!adj.has(head)) return false; // stranded — no way to continue
  // Connectivity: every remaining-edge vertex reachable from head.
  const seenV = new Set<number>([head]);
  const stack = [head];
  while (stack.length) {
    const v = stack.pop()!;
    for (const other of adj.get(v) ?? []) {
      if (!seenV.has(other)) {
        seenV.add(other);
        stack.push(other);
      }
    }
  }
  for (const v of adj.keys()) if (!seenV.has(v)) return false; // disconnected remnant
  // Parity: 0 odd → Euler circuit (ok from any incident vertex);
  //         2 odd → Euler path, head must be an endpoint; else impossible.
  const odd: number[] = [];
  for (const [v, d] of deg) if (d % 2 === 1) odd.push(v);
  if (odd.length === 0) return true;
  if (odd.length === 2) return odd.includes(head);
  return false;
}

let keyframesInjected = false;
function ensureKeyframes(): void {
  if (keyframesInjected) return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
@keyframes fudeto-dash-flow { to { stroke-dashoffset: -12; } }
@keyframes fudeto-vertex-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.4; }
}
@keyframes fudeto-start-pulse {
  0%, 100% { transform: scale(1);    opacity: 0.20; }
  50%       { transform: scale(1.45); opacity: 0.07; }
}
@keyframes fudeto-edge-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-2px); }
  40%, 80% { transform: translateX(2px); }
}
@keyframes fudeto-bubble-in {
  0%   { transform: translateY(4px) scale(0.96); opacity: 0; }
  100% { transform: translateY(0)   scale(1);    opacity: 1; }
}`;
  document.head.appendChild(style);
}

/**
 * P-2.1.2 — Edge curve fingerprint: deterministic ±1° tangent offset per edge.
 *
 * When rendering an edge as a curved path (quadratic bezier), add this angle
 * to the edge midpoint perpendicular to produce a subtle hand-drawn curve.
 * Returns radians in range [-π/180, +π/180] (i.e. ±1°).
 *
 * Exported for testability (m11-craft.test.ts).
 */
export function edgeFingerprint(seed: string, edgeId: number): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  h = Math.imul(h ^ (edgeId + 999), 16777619);
  h = Math.imul(h ^ 0xdead, 16777619);
  return ((h >>> 0) / 0xffffffff - 0.5) * (2 * Math.PI / 180); // ±1°
}

/**
 * P-2.1.3 — Global rotation fingerprint: deterministic ±3° per puzzle seed.
 *
 * Applied to the SVG root `transform: rotate()` so the entire graph is
 * slightly tilted, giving each puzzle a unique visual signature.
 * Returns radians in range [-3π/180, +3π/180] (i.e. ±3°).
 *
 * Exported for testability (m11-craft.test.ts).
 */
export function globalRotationFingerprint(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  h = Math.imul(h ^ 0xbeef, 16777619);
  return ((h >>> 0) / 0xffffffff - 0.5) * (6 * Math.PI / 180); // ±3°
}
